import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';

import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { FormTextInput, FormRadio } from 'components/form-fields';
import { Button } from 'components/buttons';
import { Table } from 'components/listing';
import { ShowHide } from 'components/layout';
import { useValidation } from 'hooks/useValidation';
import { BulkImportMappingService } from 'services';

import './SaveMappingModal.css';

const defaultArray = [];
const defaultFunction = () => { };

const badRequestCode = 400;
const mappingNameMaxLength = 255;
const saveErrorMessage = "Could not save the mapping. Please try again.";
const globalUpdateTooltip = "Only a Company Admin or Account Owner can update a global mapping";

const personalScope = 'personal';
const globalScope = 'global';

const updateExisting = 'update';
const saveAsNew = 'new';

// The create / update saved-mapping dialog for step 2 of the bulk import wizard. When
// appliedMapping is null the dialog creates a new mapping; otherwise it offers updating the
// applied mapping or saving the current grid as a new one. The columns list is seeded from
// the wizard's current grid state and rows can be removed here without affecting the grid —
// removal only trims what gets saved.
export function SaveMappingModal(
    {
        showModal = false,
        appliedMapping = null,
        canManageGlobal = false,
        microserviceKey,
        bulkImportTypeKey,
        initialColumns = defaultArray,
        onSaved = defaultFunction,
        onClose = defaultFunction
    }
) {
    const [mappingName, setMappingName] = useState("");
    const [scope, setScope] = useState(personalScope);
    const [saveMode, setSaveMode] = useState(updateExisting);
    const [columns, setColumns] = useState(defaultArray);
    const [saving, setSaving] = useState(false);
    const [isValid, errors, validate] = useValidation();

    const isUpdateMode = appliedMapping != null;
    // Updating a global mapping's record (or changing scope) needs the admin role; the
    // backend rejects it with a 403 otherwise, so the update option is disabled up front.
    const canUpdateApplied = isUpdateMode && (!appliedMapping.isGlobal || canManageGlobal);

    useEffect(seedFromProps, [showModal]);

    function seedFromProps() {
        if (!showModal) {
            return;
        }

        setMappingName(appliedMapping?.mappingName ?? "");

        let initialScope = personalScope;
        if (appliedMapping != null && appliedMapping.isGlobal && canManageGlobal) {
            initialScope = globalScope;
        }
        setScope(initialScope);

        let initialSaveMode = updateExisting;
        if (isUpdateMode && !canUpdateApplied) {
            initialSaveMode = saveAsNew;
        }
        setSaveMode(initialSaveMode);

        setColumns(initialColumns);
        setSaving(false);
    }

    useEffect(runValidation, [mappingName, showModal]);

    function runValidation() {
        if (!showModal) {
            return;
        }
        const rules = [
            { fieldName: "mappingName", required: true, maxLength: mappingNameMaxLength }
        ];
        validate(rules, { mappingName: mappingName });
    }

    function onRemoveColumn(propertyName) {
        setColumns((previousColumns) => {
            return previousColumns.filter((column) => column.propertyName !== propertyName);
        });
    }

    function onSaveClick() {
        if (!isValid || columns.length === 0 || saving) {
            return;
        }

        const mappingPlan = columns.map((column) => {
            return { key: column.sourceColumnName, value: column.propertyName };
        });
        const isGlobal = scope === globalScope;
        const isUpdate = isUpdateMode && saveMode === updateExisting;

        setSaving(true);

        let savePromise = null;
        if (isUpdate) {
            savePromise = BulkImportMappingService.updateMapping(
                microserviceKey,
                appliedMapping.bulkImportMappingId,
                { mappingName: mappingName, isGlobal: isGlobal, mappingPlan: mappingPlan }
            );
        } else {
            savePromise = BulkImportMappingService.createMapping(
                microserviceKey,
                { mappingName: mappingName, bulkImportTypeKey: bulkImportTypeKey, isGlobal: isGlobal, mappingPlan: mappingPlan }
            );
        }

        savePromise.then(
            (newMappingId) => {
                let savedMappingId = newMappingId;
                let successMessage = `Mapping '${mappingName}' has been successfully created.`;
                if (isUpdate) {
                    savedMappingId = appliedMapping.bulkImportMappingId;
                    successMessage = `Mapping '${mappingName}' has been successfully updated.`;
                }
                NotificationToaster.show(Intent.SUCCESS, successMessage);
                setSaving(false);
                onSaved(savedMappingId);
            },
            (error) => {
                if (error?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, saveErrorMessage);
                }
                setSaving(false);
            }
        );
    }

    let modalTitle = "Create mapping";
    if (isUpdateMode) {
        modalTitle = "Update mapping";
    }

    let updateTooltip = null;
    if (!canUpdateApplied) {
        updateTooltip = globalUpdateTooltip;
    }

    const saveModeOptions = [
        {
            value: updateExisting,
            label: `Update '${appliedMapping?.mappingName}'`,
            disabled: !canUpdateApplied,
            tooltip: updateTooltip
        },
        { value: saveAsNew, label: 'Save as a new mapping' }
    ];

    let scopeOptions = [{ value: personalScope, label: 'Personal - only you can see and use this mapping' }];
    if (canManageGlobal) {
        scopeOptions = scopeOptions.concat([{ value: globalScope, label: 'Global - everyone in your account can see and use this mapping' }]);
    }

    let saveButtonText = "Create mapping";
    if (isUpdateMode) {
        saveButtonText = "Save mapping";
    }

    const columnsHeaderData = {
        headers: [
            { key: 'fileColumn', value: 'FILE COLUMN' },
            { key: 'mappedToField', value: 'MAPPED TO FIELD' },
            { key: 'actions', value: '' }
        ]
    };

    const columnRows = columns.map((column) => {
        return {
            key: column.propertyName,
            cells: [
                column.sourceColumnName,
                column.friendlyName,
                <Button
                    key={`save-mapping-remove-${column.propertyName}`}
                    icon="trash"
                    iconOnly={true}
                    minimal={true}
                    large={false}
                    text="Remove column"
                    id={`save-mapping-remove-${column.propertyName}`}
                    onClick={() => onRemoveColumn(column.propertyName)}
                    disabled={saving}
                />
            ]
        };
    });

    return (
        <Modal
            isOpen={showModal}
            onClose={onClose}
            clickOutsideClose={false}
            className="save-mapping-modal"
            title={modalTitle}
            size={ModalSize.MEDIUM}
            updateStateOnClose={false}
            footer={(
                <div className="bp6-dialog-footer-actions">
                    <Button text="Cancel" intent={Intent.SECONDARY} id="save-mapping-cancel" onClick={onClose} disabled={saving} />
                    <Button
                        id="save-mapping-save"
                        intent="primary"
                        onClick={onSaveClick}
                        disabled={!isValid || columns.length === 0 || saving}
                        text={saveButtonText}
                    />
                </div>
            )}
        >
            <ShowHide
                evaluator={isUpdateMode}
                show={(
                    <FormRadio
                        id="save-mapping-mode"
                        options={saveModeOptions}
                        selectedValue={saveMode}
                        onChange={(event) => setSaveMode(event.currentTarget.value)}
                        disabled={saving}
                    />
                )}
            />

            <FormTextInput
                id="save-mapping-name"
                headingText="Mapping name"
                placeholder="Enter a name for this mapping"
                value={mappingName}
                onChange={(event) => setMappingName(event.target.value)}
                dangerHelperText={errors.mappingName}
                maxLength={mappingNameMaxLength}
                disabled={saving}
                large={true}
                fill={true}
            />

            <FormRadio
                id="save-mapping-scope"
                headingText="Level"
                options={scopeOptions}
                selectedValue={scope}
                onChange={(event) => setScope(event.currentTarget.value)}
                disabled={saving}
            />

            <h4>Columns in this mapping</h4>
            <ShowHide
                evaluator={columns.length > 0}
                show={(
                    <div id="save-mapping-columns" className="save-mapping-columns-list">
                        <Table headerData={columnsHeaderData} data={columnRows} shrinkLastColumn={true} />
                    </div>
                )}
                hide={(
                    <p className="bp6-text-muted">All columns have been removed. Map at least one column to save this mapping.</p>
                )}
            />
        </Modal>
    );
}

SaveMappingModal.propTypes = {
    showModal: PropTypes.bool,
    appliedMapping: PropTypes.object,
    canManageGlobal: PropTypes.bool,
    microserviceKey: PropTypes.string,
    bulkImportTypeKey: PropTypes.string,
    initialColumns: PropTypes.array,
    onSaved: PropTypes.func,
    onClose: PropTypes.func
};
